/*
 * hsp-quick-add.js — the mobile "Choose Options" bottom sheet.
 *
 * Pairs with templates/product.quickadd.liquid, snippets/hsp-quick-add-sheet.liquid
 * and assets/hsp-quick-add.css.
 *
 * WHAT THIS REPLACES, AND WHY.
 *
 * Below 750px this takes over from Horizon's quick-add modal (assets/quick-add.js
 * + snippets/quick-add-modal.liquid). That modal fetched the WHOLE product page,
 * cloned [data-product-grid-content] out of it and reshaped the clone in JS. The
 * result, measured at 375x812 on the Poly Savi 8420: every block in the sheet was
 * squeezed below its own content height, variant-picker got a 309px box holding
 * 812px of options, and because scrollHeight is computed from the shrunken boxes
 * the maximum scroll was 106px against ~500px of hidden content. Five of that
 * product's six purchasable options could not be reached at any scroll position.
 *
 * Here the payload is a purpose-built fragment and the sheet is three rows with
 * one scroller, so that class of bug cannot recur. See hsp-quick-add.css.
 *
 * Written as a classic IIFE, not an ES module, matching hp-collection-search.js.
 * It must not depend on Horizon's importmap.
 *
 * DEGRADATION: if this file fails to load or throws, nothing intercepts the
 * trigger and the stock modal opens exactly as before. That is deliberate.
 */
(function () {
  'use strict';

  /* Horizon's own isMobileBreakpoint is !matchMedia('(min-width: 750px)').
     Same line, expressed the same way, so there is one breakpoint in play. */
  var MOBILE_QUERY = '(max-width: 749px)';
  var DIALOG_ID = 'hsp-quick-add-dialog';
  var NO_INDEX = -1;

  /** handle -> fragment HTML. Cleared on cart update, like quick-add.js does. */
  var cache = Object.create(null);

  var dialog = null;
  var savedScrollY = 0;
  var fetchToken = 0;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  /* ------------------------------------------------------------------ *
   * Body scroll lock
   *
   * showModal() makes the page inert but does NOT stop it scrolling on iOS.
   * This is the same position:fixed + negative-top trick assets/dialog.js uses
   * for the stock modal, so the two behave identically and there is only one
   * idiom in the theme. hsp-mobile-fixes.liquid already sets
   * `html { scrollbar-gutter: stable }` to absorb the layout shift this causes.
   * ------------------------------------------------------------------ */

  function lockBody() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + savedScrollY + 'px';
  }

  function unlockBody() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, savedScrollY);
  }

  /* ------------------------------------------------------------------ *
   * Dialog shell
   * ------------------------------------------------------------------ */

  function ensureDialog() {
    if (dialog && document.body.contains(dialog)) return dialog;

    dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'hqa-dialog';

    // Tapping the backdrop closes. A <dialog> reports backdrop clicks as
    // clicks on the dialog itself, so anything that hits a descendant is not
    // the backdrop.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeSheet();
    });

    // Escape fires `cancel`; route it through closeSheet so the body lock is
    // released and the close animation runs.
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeSheet();
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  function closeSheet() {
    if (!dialog || !dialog.open) return;
    dialog.classList.add('is-closing');
    var done = function () {
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
      unlockBody();
    };
    // Fall back to a timer if the animation is disabled (reduced motion).
    var settled = false;
    dialog.addEventListener('animationend', function handler() {
      dialog.removeEventListener('animationend', handler);
      if (settled) return;
      settled = true;
      done();
    });
    setTimeout(function () {
      if (settled) return;
      settled = true;
      done();
    }, 260);
  }

  /* ------------------------------------------------------------------ *
   * Fetch + open
   * ------------------------------------------------------------------ */

  function fragmentUrl(handle) {
    return '/products/' + encodeURIComponent(handle) + '?view=quickadd';
  }

  function openSheet(handle) {
    var d = ensureDialog();
    var token = ++fetchToken;

    d.innerHTML = '<div class="hqa-loading" role="status" aria-label="Loading options"></div>';
    if (!d.open) {
      lockBody();
      d.showModal();
    }

    var cached = cache[handle];
    if (cached) {
      inject(cached, token);
      return;
    }

    fetch(fragmentUrl(handle), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        cache[handle] = html;
        inject(html, token);
      })
      .catch(function (error) {
        if (token !== fetchToken) return;
        console.warn('[hsp-quick-add] could not load sheet, sending shopper to the product page', error);
        // Never strand them in an empty sheet — the product page always works.
        window.location.href = '/products/' + encodeURIComponent(handle);
      });
  }

  function inject(html, token) {
    // A newer tap has already superseded this one.
    if (token !== fetchToken || !dialog) return;
    dialog.innerHTML = html;
    var root = dialog.querySelector('[data-hqa]');
    if (!root) return;
    wire(root);
  }

  /* ------------------------------------------------------------------ *
   * Variant matrix
   *
   * Shape is documented in snippets/hp-vp-matrix.liquid. It rides along inside
   * <variant-picker> because variant-main-picker.liquid renders it there, so
   * there is no second payload to keep in sync.
   * ------------------------------------------------------------------ */

  function readMatrix(root) {
    var node = root.querySelector('script[data-hp-vp-matrix]');
    if (!node || !node.textContent) return null;
    var matrix;
    try {
      matrix = JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[hsp-quick-add] unreadable variant matrix', error);
      return null;
    }
    if (!matrix || !Array.isArray(matrix.o) || !Array.isArray(matrix.x)) return null;

    matrix.axisCount = matrix.o.length;
    matrix.idIndex = new Map();
    matrix.o.forEach(function (ids, axis) {
      ids.forEach(function (id, index) {
        matrix.idIndex.set(String(id), [axis, index]);
      });
    });
    return matrix;
  }

  /**
   * Which value is chosen on each axis, as indices into matrix.o.
   *
   * Seeded with -1 ("nothing chosen"), then every SINGLE-value axis is pinned
   * to 0. That pinning is load-bearing: variant-main-picker.liquid skips any
   * option with fewer than two values (:107-109) so it has no control in the
   * DOM, but hp-vp-matrix emits EVERY option, so row arity always equals the
   * full option count. Without the pin those axes would stay -1 and no row
   * would ever match.
   *
   * Anything still -1 afterwards is a real axis the shopper has not answered.
   */
  function readCombo(root, matrix) {
    var combo = new Array(matrix.axisCount).fill(NO_INDEX);

    matrix.o.forEach(function (ids, axis) {
      if (ids.length === 1) combo[axis] = 0;
    });

    root.querySelectorAll('input[type="radio"][data-option-value-id]').forEach(function (input) {
      if (!input.checked) return;
      var at = matrix.idIndex.get(String(input.getAttribute('data-option-value-id')));
      if (at) combo[at[0]] = at[1];
    });

    root.querySelectorAll('select.hp-vp__select').forEach(function (select) {
      var option = select.options[select.selectedIndex];
      if (!option) return;
      var at = matrix.idIndex.get(String(option.getAttribute('data-option-value-id')));
      if (at) combo[at[0]] = at[1];
    });

    return combo;
  }

  function findRow(matrix, combo) {
    for (var i = 0; i < matrix.x.length; i += 1) {
      var row = matrix.x[i];
      var hit = true;
      for (var axis = 0; axis < matrix.axisCount; axis += 1) {
        if (combo[axis] !== row[axis]) { hit = false; break; }
      }
      if (hit) return row;
    }
    return null;
  }

  /**
   * Human label per axis, for "Choose a Platform".
   *
   * Built by walking the rendered controls and resolving ONE of their option
   * value ids through the matrix, rather than trusting data-fieldset-index —
   * that attribute counts rendered fieldsets, so it drifts out of alignment
   * with the matrix on any product that has a single-value option.
   */
  function readAxisLabels(root, matrix) {
    var labels = new Array(matrix.axisCount).fill('');

    root.querySelectorAll('[data-option-name]').forEach(function (group) {
      var probe = group.querySelector('[data-option-value-id]');
      if (!probe) return;
      var at = matrix.idIndex.get(String(probe.getAttribute('data-option-value-id')));
      if (!at) return;
      var labelEl = group.querySelector('.hp-vp__label');
      labels[at[0]] = labelEl
        ? labelEl.textContent.trim()
        : (group.getAttribute('data-option-name') || '').replace(/-/g, ' ');
    });

    return labels;
  }

  /* ------------------------------------------------------------------ *
   * Money
   * ------------------------------------------------------------------ */

  function money(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function wire(root) {
    var picker = root.querySelector('variant-picker');
    var matrix = readMatrix(root);
    var labels = matrix ? readAxisLabels(root, matrix) : [];
    var currency = root.getAttribute('data-currency') || 'USD';

    var el = {
      priceNow: root.querySelector('[data-hqa-price-now]'),
      priceWas: root.querySelector('[data-hqa-price-was]'),
      totalNow: root.querySelector('[data-hqa-total-now]'),
      totalWas: root.querySelector('[data-hqa-total-was]'),
      notice: root.querySelector('[data-hqa-notice]'),
      atc: root.querySelector('[data-hqa-atc]'),
      atcLabel: root.querySelector('[data-hqa-atc-label]'),
      qtyInput: root.querySelector('[data-hqa-qty-input]')
    };

    neutralisePicker(picker);
    bindLegendUpdater(root);
    if (picker) {
      clearPreselection(root, matrix);
      // Once up front as well as on change: clearPreselection blanks the
      // fieldset legends itself, but a select-mode axis only has its
      // selectedIndex reset and would otherwise keep its server-rendered value.
      syncLegends(root);
    }

    root.querySelector('[data-hqa-close]').addEventListener('click', closeSheet);

    /* ---- quantity ---- */
    var qtyMin = Number(el.qtyInput.getAttribute('min')) || 1;

    function currentQty() {
      var n = parseInt(el.qtyInput.value, 10);
      if (!Number.isFinite(n) || n < qtyMin) n = qtyMin;
      return n;
    }

    root.querySelectorAll('[data-hqa-qty]').forEach(function (button) {
      button.addEventListener('click', function () {
        var next = currentQty() + Number(button.getAttribute('data-hqa-qty'));
        el.qtyInput.value = Math.max(qtyMin, next);
        sync();
      });
    });
    el.qtyInput.addEventListener('change', sync);

    /* ---- selection -> price + validation ----
       Bound on the ROOT, not the picker, so it survives any morph the picker
       does to its own subtree. */
    root.addEventListener('change', sync);

    function sync() {
      var qty = currentQty();
      root.querySelector('[data-hqa-qty="-1"]').disabled = qty <= qtyMin;

      if (!matrix) return; // no matrix: leave the server-rendered price alone

      var combo = readCombo(root, matrix);
      var missing = [];
      for (var axis = 0; axis < matrix.axisCount; axis += 1) {
        if (combo[axis] === NO_INDEX) missing.push(labels[axis] || 'an option');
      }

      if (missing.length) {
        setState(false, 'Choose ' + listify(missing) + ' to continue.');
        return;
      }

      var row = findRow(matrix, combo);
      if (!row) {
        setState(false, "That combination isn't available. Try another option.");
        return;
      }

      var variantId = row[matrix.axisCount];
      var available = row[matrix.axisCount + 1] === 1;
      var unit = row[matrix.axisCount + 2];
      // 0 means "not on sale" — never null, so the JSON can't break. Treating
      // it as a real compare-at would show every product as 100% off.
      var was = row[matrix.axisCount + 3] || 0;

      root.setAttribute('data-variant-id', String(variantId));

      write(el.priceNow, money(unit, currency));
      toggleStrike(el.priceWas, was > unit, money(was, currency));
      write(el.totalNow, money(unit * qty, currency));
      toggleStrike(el.totalWas, was > unit, money(was * qty, currency));

      setState(available, available ? '' : 'That option is sold out.');
    }

    function setState(enabled, message) {
      el.atc.disabled = !enabled;
      el.atc.setAttribute('aria-disabled', String(!enabled));
      if (message) {
        el.notice.textContent = message;
        el.notice.hidden = false;
      } else {
        el.notice.textContent = '';
        el.notice.hidden = true;
      }
    }

    /* ---- add to cart ---- */
    el.atc.addEventListener('click', function () {
      if (el.atc.disabled) return;
      addToCart(root.getAttribute('data-variant-id'), currentQty(), el);
    });

    sync();
  }

  function write(node, text) {
    if (node) node.textContent = text;
  }

  function toggleStrike(node, show, text) {
    if (!node) return;
    node.hidden = !show;
    if (show) node.textContent = text;
  }

  /* No article: "Choose a Platform" and "Choose Platform and Remote answer"
     would disagree with each other, and the axis names are already the labels
     the shopper is looking at. */
  function listify(items) {
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /* ------------------------------------------------------------------ *
   * Picker neutralisation
   *
   * The <variant-picker> wrapper is kept deliberately — .hp-vp-root carries
   * every CSS custom property, the whole selected-state ruleset and the
   * grouped-axis filter, and hp-variant-logic.js bails without it. But two of
   * its behaviours are wrong inside a sheet on a collection page.
   * ------------------------------------------------------------------ */

  function neutralisePicker(picker) {
    if (!picker) return;

    /* 1. variant-picker.js:62-65 treats a picker as "on the product page" when
          data-template-product-match is set and the change did not originate
          inside a product-card or a quick-add-dialog. Our sheet is neither, and
          the quickadd template renders the same product it is picking for — so
          the flag IS emitted (variant-main-picker.liquid:73-75) and the picker
          would history.replaceState ?variant=... onto the COLLECTION url. On a
          combined listing it goes further and morphs the collection's <main>
          with the product page's. Removing the attribute also no-ops
          hp-variant-logic's syncUrl(), which is equally unwanted here. */
    picker.removeAttribute('data-template-product-match');

    /* 2. Every option tap would otherwise refetch the entire product page:
          buildRequestUrl only appends section_id for a hardcoded set of
          ancestors (variant-picker.js:213-217), none of which we have. The
          matrix already carries id, availability and price, so the fetch buys
          nothing. Instance assignment shadows the prototype method — it is
          public, not #private. */
    picker.fetchUpdatedSection = function () {};

    /* 3. Stubbing (2) means 'variant:update' never fires, and that is what
          hp-variant-logic listens on to re-run refresh() after a morph — so
          compatibility marking and the "+$58" deltas would go stale after the
          first tap. paintSelection() and syncGroupFromSelection() run
          synchronously on change and are unaffected; this restores the rest.
          The instance is parked on the element at hp-variant-logic.js:78. */
    picker.addEventListener('change', function () {
      if (picker.hpVariantLogic && typeof picker.hpVariantLogic.refresh === 'function') {
        picker.hpVariantLogic.refresh();
      }
    });
  }

  /**
   * Repaint the "Platform — UC" value beside every axis legend.
   *
   * The picker ships its own updater as an inline <script> resolved with
   * document.currentScript (variant-main-picker.liquid:584-607). innerHTML does
   * not execute injected scripts, so it never runs here.
   *
   * This is NOT a port of that handler. The original reads event.target and
   * updates only the axis the shopper touched, which is wrong for this sheet:
   * hp-variant-logic auto-repairs an impossible combination by re-checking an
   * input on a DIFFERENT axis programmatically, and a programmatic .checked
   * assignment fires no change event. On the product page the picker papers
   * over that by refetching the section and replacing the legend markup with
   * server truth — which is exactly the fetch neutralisePicker() stubs out.
   *
   * Observed before this was rewritten: choosing MS Teams then Cisco EHS
   * (a pair that does not exist) correctly repaired to UC, lit the UC card and
   * announced the change, while the legend went on reading "MS Teams".
   *
   * So derive every legend from what is actually checked, not from the event.
   * Same cost, and it cannot drift.
   */
  function syncLegends(root) {
    root.querySelectorAll('[data-option-name]').forEach(function (group) {
      var display = group.querySelector('[data-selected-value]');
      if (!display) return;

      var value = '';
      var select = group.querySelector('select.hp-vp__select');
      if (select) {
        var option = select.options[select.selectedIndex];
        value = option ? (option.getAttribute('value') || option.textContent) : '';
      } else {
        var checked = group.querySelector('input[type="radio"]:checked');
        value = checked ? (checked.value || checked.getAttribute('aria-label') || '') : '';
      }
      display.textContent = String(value).trim();
    });
  }

  /* Bubble phase on the root. hp-variant-logic repairs synchronously in its own
     capture-phase handler on the picker, so by the time this runs the DOM has
     already settled. Kept separate from sync() so legends still track even on a
     product with no matrix to price against. */
  function bindLegendUpdater(root) {
    root.addEventListener('change', function () {
      syncLegends(root);
    });
  }

  /**
   * Start every multi-value axis unanswered.
   *
   * Liquid always pre-selects selected_or_first_available_variant, so without
   * this the sheet opens with a full combination already chosen and a tap on
   * "Add to cart" silently buys a default. sections/main-collection.liquid:608-614
   * already takes exactly this position for the card button — "We never
   * silently add the first variant for multi-variant products (creates
   * 'wrong size/color' CS issues)" — and a sheet that pre-answers the question
   * it exists to ask undoes that.
   *
   * Single-value axes are left alone: they are pinned in readCombo and have no
   * control rendered.
   */
  function clearPreselection(root, matrix) {
    if (!matrix) return;

    root.querySelectorAll('fieldset[data-option-name]').forEach(function (fieldset) {
      var probe = fieldset.querySelector('[data-option-value-id]');
      var at = probe && matrix.idIndex.get(String(probe.getAttribute('data-option-value-id')));
      if (at && matrix.o[at[0]].length < 2) return;

      fieldset.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = false;
        input.setAttribute('data-current-checked', 'false');
        input.setAttribute('data-previous-checked', 'false');
      });
      // The picker paints the selected card from a class as well as :checked,
      // so the class has to go too or a deselected row stays filled navy.
      fieldset.querySelectorAll('.hp-vp__card--on').forEach(function (card) {
        card.classList.remove('hp-vp__card--on');
      });
      var display = fieldset.querySelector('[data-selected-value]');
      if (display) display.textContent = '';
    });

    root.querySelectorAll('select.hp-vp__select').forEach(function (select) {
      select.selectedIndex = -1;
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */

  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (node) {
      var id = node.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    var go = function () {
      if (typeof drawer.open === 'function') drawer.open();
    };
    /* cart-drawer.js:52 gates its own handler on `event instanceof CartAddEvent`,
       an ES-module class a classic script cannot construct — so dispatching a
       plain CustomEvent will never open the drawer by itself. Open it directly.
       Same approach as sections/main-collection.liquid:2512-2521. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer-component').then(go);
    } else {
      go();
    }
  }

  function errorMessage(data) {
    if (!data) return null;
    if (data.message) return data.message;
    if (data.description) return data.description;
    if (typeof data.errors === 'string') return data.errors;
    if (data.errors && typeof data.errors === 'object') {
      return Object.keys(data.errors).map(function (k) { return data.errors[k]; }).join(' ');
    }
    return null;
  }

  function addToCart(variantId, quantity, el) {
    if (!variantId) return;

    var original = el.atcLabel.textContent;
    el.atc.classList.add('is-busy');
    el.atcLabel.textContent = 'Adding…';

    var payload = {
      id: Number(variantId),
      quantity: quantity,
      sections: collectCartSectionIds().join(','),
      sections_url: window.location.pathname
    };

    /* Literal path, not Theme.routes.cart_add_url: `Theme` is declared with
       const in a classic inline script (snippets/scripts.liquid:306), so it is
       a lexical global rather than a property of window. */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (data) {
          // Shopify signals failure with 4xx OR a 200 body carrying status/errors.
          // A successful single-item add returns the LINE ITEM, which has no status.
          var failed = !res.ok || (data && (data.status || data.errors));
          if (failed) throw new Error(errorMessage(data) || 'Could not add to cart');
          return data;
        });
      })
      .then(function (line) {
        /* One /cart.js, once. product-form.js:253-263 inspects
           detail.resource for `.items`; the /cart/add.js response is a line
           item and has none, so every product-form-component on the page would
           fire its own /cart.js to compensate — up to 24 redundant requests on
           a full collection grid. Handing it a real cart short-circuits that. */
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .catch(function () { return null; })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart || line,
                sourceId: 'hsp-quick-add',
                data: {
                  source: 'hsp-quick-add',
                  /* A TOTAL, not a delta: cart-icon.js:56-61 assigns rather than
                     increments whenever source is not 'product-form-component'. */
                  itemCount: cart ? cart.item_count : null,
                  sections: line && line.sections
                }
              }
            }));
            cache = Object.create(null);
            closeSheet();
            openCartDrawer();
          });
      })
      .catch(function (error) {
        el.notice.textContent = error.message || 'Could not add to cart.';
        el.notice.hidden = false;
      })
      .then(function () {
        el.atc.classList.remove('is-busy');
        el.atcLabel.textContent = original;
      });
  }

  /* ------------------------------------------------------------------ *
   * Trigger interception
   *
   * Capture phase on document, so it lands before the bubble-phase handlers in
   * sections/main-collection.liquid (:2468 quick view, :2564 add to cart).
   * stopPropagation() during capture at document keeps the event from reaching
   * any node further down the path, so those never fire.
   *
   * Deliberately NOT hooked on .quick-add__button: Horizon's on:click
   * delegation is itself a document capture listener registered during module
   * execution (component.js:287), which a deferred classic script cannot beat.
   * The outer triggers carry no on:click, so there is nothing to race.
   *
   * The media query is read at click time rather than cached, so a desktop
   * viewport always falls through to the stock modal.
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    if (!isMobile()) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var trigger = target.closest('[data-quickview-trigger],[data-hp-quickview],[data-atc-trigger]');
    if (!trigger || trigger.disabled) return;

    /* Single-variant products keep their direct-add path in main-collection.liquid.
       There is no choice to present, so a sheet would only add a step. */
    if (trigger.hasAttribute('data-atc-trigger') &&
        Number(trigger.getAttribute('data-variants-count') || '1') <= 1) {
      return;
    }

    var handle = trigger.getAttribute('data-product-handle');
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();
    openSheet(handle);
  }, true);

  /* Crossing into desktop while the sheet is open would leave a mobile-only
     surface on a layout it was never designed for. Mirrors what quick-add.js
     does with mediaQueryLarge. */
  var desktopQuery = window.matchMedia('(min-width: 750px)');
  var onBreakpoint = function (event) {
    if (event.matches) closeSheet();
  };
  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', onBreakpoint);
  } else if (desktopQuery.addListener) {
    desktopQuery.addListener(onBreakpoint);
  }
})();
